export type PersistedChunk = {
  key: string;
  appointmentId: number;
  recordingId: string;
  chunkIndex: number;
  mimeType: string;
  blob: Blob;
  attempts: number;
  createdAt: number;
};

const DB_NAME = 'ai-notes-upload-queue';
const STORE_NAME = 'chunks';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('byIdentity', 'recordingId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });

  return dbPromise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function putPersistedChunk(chunk: PersistedChunk): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).put(chunk);
  return txDone(tx);
}

export async function deletePersistedChunk(key: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).delete(key);
  return txDone(tx);
}

export async function listPersistedChunks(recordingId: string): Promise<PersistedChunk[]> {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('byIdentity');

  const items = await new Promise<PersistedChunk[]>((resolve, reject) => {
    const request = index.getAll(recordingId);
    request.onsuccess = () => resolve(request.result ?? []);
    request.onerror = () => reject(request.error);
  });

  return items.sort((a, b) => a.chunkIndex - b.chunkIndex);
}

export async function clearPersistedChunks(recordingId: string): Promise<void> {
  const db = await getDb();
  const items = await listPersistedChunks(recordingId);
  if (items.length === 0) return;

  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  for (const item of items) {
    store.delete(item.key);
  }
  return txDone(tx);
}
